/**
 * Project Configuration System
 *
 * Single source of truth for all project-specific values.
 * Replaces hardcoded Track IDs, API URLs, feature flags, etc.
 */

import { createContext } from 'react'
import { useContext } from 'react'
// Type-only import — erased at compile time, so the tile-map library does
// NOT enter the initial bundle (project.ts is in the eager graph; the lib
// itself stays in the lazy map chunk).
import type { ArtrackWebConfig } from '@apopovic77/tile-map'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Map-viewer-specific extras a project may carry (calibration, physics,
 *  overlays, kiosk, …). Everything the tile-map viewer needs beyond what
 *  ProjectConfig already models. projectId/projectName/api are always
 *  derived from the ProjectConfig itself — see buildTileMapConfig(). */
export type TileMapExtras = Omit<Partial<ArtrackWebConfig>, 'projectId' | 'projectName' | 'api'>

export interface ProjectConfig {
  id: string
  name: string

  api: {
    artrackUrl: string
    storageUrl: string
    /** Knowledge API base (optional — projects without species/knowledge omit it) */
    knowledgeUrl?: string
    apiKey: string
    trackId: number
  }

  map: {
    type: 'tiles' | 'osm' | 'blank'
    tiles?: {
      manifest: string
      calibration: string
    }
    center: { lat: number; lng: number }
    defaultZoom: number
    backgroundColor: string
  }

  pins: {
    defaultStyle: 'dot' | 'teardrop' | 'badge' | 'marker'
    categoryIcons: Record<string, string>
  }

  theme: {
    primary: string
    background: string
    text: string
    fontHeading: string
    fontBody: string
    logo?: string
  }

  features: {
    audioGuide: boolean
    stories: boolean
    kioskMode: boolean
    species: boolean
    navigation: boolean
    routeIntelligence: boolean
    threeD: boolean
    /**
     * Spielt DIESE App die selbstlaufende GPS-Erzaehlung entlang des Weges?
     *
     * `false` heisst: Ein anderer spielt sie — im iOS-Webview die native App,
     * die dafuer einen eigenen Naeherungs-Algorithmus mitbringt und bei
     * gesperrtem Bildschirm weiterlaufen kann, was ein Browser nicht kann
     * (Alex 2026-08-20). Die Web-Seite schweigt dann bewusst; das ist kein
     * Ausfall, sondern die Arbeitsteilung.
     *
     * Fehlt der Wert, bleibt es beim bisherigen Verhalten (an). Zur Laufzeit
     * kann der Wirt es zusaetzlich uebernehmen (`audio:narration:native`) —
     * dieser Schalter ist der Weg, es OHNE Wirt abzuschalten, etwa zum Pruefen.
     */
    storyNarration?: boolean
  }

  /** Bounding box for GPS area detection */
  bounds?: { minLat: number; maxLat: number; minLng: number; maxLng: number }
  /** Default route ID to use for navigation */
  defaultRouteId?: number
  /** All route IDs for this track */
  routeIds?: number[]

  /** Map-viewer extras (tile manifest details, calibration, physics,
   *  svg overlays, kiosk, …). Optional: projects without them get a
   *  viewer config synthesized from `map` alone. One tenant = ONE file:
   *  this replaces the former hardcoded tileMapConfig.ts second config. */
  tileMap?: TileMapExtras

  /** Entdecken/Discover surface tuning. */
  discover?: {
    /** Highlight carousel: listed waypoint ids come FIRST (in this order),
     *  the rest keeps the natural curated order (Alex 114564: Tschaukofall
     *  vorne). */
    highlightOrder?: number[]
    /** Which MAP the discover surfaces link to — hero "Karte öffnen",
     *  "Auf der Karte", ?focus= deep-links and the footer map tab.
     *  ONE switch flips everything to the 3D map later; default stays the
     *  2D tile map (Alex 2026-07-20: "anfangs jetzt noch alles 2d"). */
    mapPath?: '/navigate-tile' | '/navigate-3d'
  }

  /** Curated route presentation (picker order, marker pill subtitles,
   *  highlight teardrop). Formerly hardcoded in NavigatePage — extracted
   *  for the tenant template (Post 4242). All optional: omitted values
   *  degrade to neutral defaults (natural order, no subtitles/highlight). */
  routesCuration?: {
    /** Picker order by route id; unlisted ids keep natural order at the end. */
    order?: number[]
    /** Subtitle under the Start teardrop pill (e.g. the starting venue). */
    startSubtitle?: string
    /** Pill title of the Start teardrop (default 'Start'). */
    startTitle?: string
    /** Bulb glyph of the Start teardrop (default location dot). */
    startGlyph?: 'dot' | 'waterfall' | 'i' | 'euro'
    /** Start teardrop position override — where the visitor experience
     *  really starts (ticket booth / paid entrance), when that differs from
     *  the route polyline's first point. Route geometry stays untouched. */
    startMarker?: { lat: number; lng: number }
    /** Der ArTrack-Wegpunkt HINTER dem Start-Tropfen. Kassa und Empfangshaus
     *  sind echte POI mit Text und Medien; ihr Ballon ist nur render-abge-
     *  schaltet (`display.enabled = false`), weil der Tropfen ihn ersetzt.
     *  Ohne diese Zuordnung fuehrte ein Tipp nur die Kamera hin — der Besucher
     *  stand vor einem Pin, der nichts erzaehlt (Alex 2026-08-10). */
    startMarkerWaypointId?: number
    /** Subtitle under the Ziel pill, per route id. */
    zielSubtitle?: Record<number, string>
    /** Extra highlight teardrop (e.g. a waterfall), per route id. */
    highlight?: Record<number, { lat: number; lng: number; title: string }>
    /** Route-independent extra teardrops in full Start/Ziel style (e.g. the
     *  Besucherzentrum "Start / Ende" at route km 0) — shown for EVERY route. */
    extraMarkers?: Array<{ lat: number; lng: number; title: string; subtitle?: string; color?: string; glyph?: 'dot' | 'waterfall' | 'i' | 'euro'; scale?: number; waypointId?: number }>
  }

  /** 3D presenter embed (Bridge Contract v0.2, Post 4263). `georef` carries
   *  the geo→model calibration into the frame's `init` — values come from the
   *  one-time WPT↔ArTrack calibration (contract pt. 6); absent = frame uses
   *  its provisional georef. */
  presenter3d?: {
    /** Frame-URL der kuratierten GLB-Welt (`?app=map&embed=1`). */
    frameUrl?: string
    georef?: {
      originLat: number
      originLng: number
      metersPerUnit: number
      rotationDeg: number
      originModel: { x: number; y: number; z: number }
      /** +Z = north in the GLB (Tscheppa terrain). Contract §6 addendum. */
      zNorthPositive?: boolean
    }
  }

  /** Generische Streaming-Terrain-Welt (`?app=terrain`, Post 4704) — gleicher
   *  Bridge-Vertrag wie presenter3d, geo-nativ (keine Georeferenz nötig). */
  /** v0.5 Track-LOD (#1159): Register für setTracks — Schwellen in echten
   *  Metern, bindend 0 < detailMeter < silhouetteMeter. Fehlt der Eintrag,
   *  leitet der Feed EINEN Track aus trackId/map.center ab. */
  tracks3d?: Array<{ trackId: number; name: string; lat: number; lng: number; detailMeter: number; silhouetteMeter: number }>

  terrain3d?: {
    frameUrl?: string
  }
}

// ---------------------------------------------------------------------------
// Project registry
// ---------------------------------------------------------------------------

import { tscheppaConfig } from './projects/tscheppa'
import { makerspaceConfig } from './projects/makerspace'
import { osmdevConfig } from './projects/osmdev'
import { udoConfig } from './projects/udo'
import { kaerntenConfig } from './projects/kaernten'

const PROJECTS: Record<string, ProjectConfig> = {
  tscheppa: tscheppaConfig,
  makerspace: makerspaceConfig,
  // dev-only OSM-adapter verification tenant (?project=osmdev)
  osmdev: osmdevConfig,
  // Udo Jürgens Pfad Klagenfurt — Ausschreibungs-Demo (Post 4242)
  udo: udoConfig,
  // Kärnten-Grundkarte auf eigenem Quadtree (?project=kaernten, Demo 2026-08-14)
  kaernten: kaerntenConfig,
}

// ---------------------------------------------------------------------------
// Resolution: VITE_PROJECT env → ?project= URL param → default
// ---------------------------------------------------------------------------

/** Map subdomains to project IDs */
const SUBDOMAIN_MAP: Record<string, string> = {
  'makerspace': 'makerspace',
  'tschepp': 'tscheppa',
  'tscheppaschlucht': 'tscheppa',
  // Kaernten-Demo auf eigener Subdomain (Alex 2026-08-14) — Produktiv-Domain
  // bleibt frei von Demo-Slugs, der Mieter haengt am Hostnamen.
  'kaernten': 'kaernten',
}

function resolveProjectId(): string {
  // 1. Build-time env
  const envProject = (import.meta as unknown as { env?: { VITE_PROJECT?: string } }).env?.VITE_PROJECT
  if (envProject && PROJECTS[envProject]) return envProject

  // 2. URL parameter (runtime)
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    const urlProject = params.get('project')
    if (urlProject && PROJECTS[urlProject]) return urlProject

    // 3. Subdomain detection (e.g. makerspace.arkturian.com)
    const host = window.location.hostname
    const subdomain = host.split('.')[0]
    if (subdomain && SUBDOMAIN_MAP[subdomain] && PROJECTS[SUBDOMAIN_MAP[subdomain]]) {
      return SUBDOMAIN_MAP[subdomain]
    }
  }

  // 4. Default
  return 'tscheppa'
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

let _config: ProjectConfig | null = null
let _projectId: string | null = null

export function getConfig(): ProjectConfig {
  if (!_config) {
    _projectId = resolveProjectId()
    _config = PROJECTS[_projectId]
  }
  return _config
}

/** The map route the discover surfaces (and footer map tab) link to —
 *  config-switchable between the 2D tile map and the 3D presenter map.
 *  Defaults to 2D (Alex 2026-07-20). */
export function getMapPath(): '/navigate-tile' | '/navigate-3d' {
  return getConfig().discover?.mapPath ?? '/navigate-tile'
}

export function getProjectId(): string {
  if (!_projectId) getConfig()
  return _projectId!
}

/** Check if a specific project is active */
export function isProject(id: string): boolean {
  return getProjectId() === id
}

// ---------------------------------------------------------------------------
// React Context
// ---------------------------------------------------------------------------

export const ProjectContext = createContext<ProjectConfig>(tscheppaConfig)

export function useProject(): ProjectConfig {
  return useContext(ProjectContext)
}

// ---------------------------------------------------------------------------
// Theme injection
// ---------------------------------------------------------------------------

export function injectTheme(config: ProjectConfig) {
  const root = document.documentElement
  root.style.setProperty('--ar-color-primary', config.theme.primary)
  root.style.setProperty('--ar-color-bg', config.theme.background)
  root.style.setProperty('--ar-color-text', config.theme.text)
  root.style.setProperty('--ar-font-heading', config.theme.fontHeading)
  root.style.setProperty('--ar-font-body', config.theme.fontBody)
}
